import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

function PartnerFlowConfigsTab({ partner, onViewFlows }) {
  const [flowConfigs, setFlowConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortText: '',
    isSequential: false,
    canRerun: true,
    pointsOnCompletion: 500,
    pointsExpirationType: 'relative',
    pointsExpirationValue: 60,
    expirationType: 'relative',
    expirationValue: 30,
    expiresAt: '',
    steps: []
  });
  const [message, setMessage] = useState(null);
  const [actionConfigs, setActionConfigs] = useState([]);

  useEffect(() => {
    if (partner) {
      fetchFlowConfigs();
      fetchActionConfigs();
    }
  }, [partner]);

  const fetchFlowConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.getPartnerFlowConfigs(partner._id);
      setFlowConfigs(response.data.flowConfigs || []);
    } catch (err) {
      console.error('Error fetching flow configs:', err);
      setMessage({ type: 'error', text: 'Failed to load flow configurations' });
    } finally {
      setLoading(false);
    }
  };

  const fetchActionConfigs = async () => {
    try {
      const response = await api.getPartnerActionConfigs(partner._id);
      setActionConfigs(response.data.actionConfigs || []);
    } catch (err) {
      console.error('Error fetching action configs:', err);
    }
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setFormData({
      name: '',
      description: '',
      shortText: '',
      isSequential: false,
      canRerun: true,
      pointsOnCompletion: 0,
      pointsExpirationType: 'never',
      pointsExpirationValue: 60,
      expirationType: 'relative',
      expirationValue: 30,
      expiresAt: '',
      steps: [{
        goal: '',
        description: '',
        instructions: '',
        url: '',
        type: 'action',
        stepType: 'action',
        actionConfigId: '',
        criteria: {
          type: 'equal',
          value: 1,
          maxValue: 5
        },
        pointsOnCompletion: {
          amount: 0,
          expiration: {
            type: 'never',
            value: 0
          }
        },
        expirationOffset: null
      }]
    });
    setShowForm(true);
    setMessage(null);
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    
    // Map existing steps to include proper structure
    const mappedSteps = (config.steps || []).map(step => ({
      goal: step.goal || '',
      description: step.description || '',
      navigation: step.navigation || '',
      instructions: step.instructions || '',
      url: step.url || '',
      type: step.stepType || step.type || 'action',
      stepType: step.stepType || step.type || 'action',
      actionConfigId: step.actionConfigId || '',
      criteria: {
        type: step.criteria?.type || 'equal',
        value: step.criteria?.value || 1,
        maxValue: step.criteria?.maxValue || 5
      },
      pointsOnCompletion: {
        amount: step.pointsOnCompletion?.amount || 0,
        expiration: {
          type: step.pointsOnCompletion?.expiration?.type || 'never',
          value: step.pointsOnCompletion?.expiration?.value || 0
        }
      },
      expirationOffset: step.expirationOffset !== undefined ? step.expirationOffset : null
    }));
    
    setFormData({
      name: config.name || '',
      description: config.description || '',
      shortText: config.shortText || '',
      isSequential: config.isSequential || config.sequential || false,
      canRerun: config.canRerun !== undefined ? config.canRerun : true,
      pointsOnCompletion: config.pointsOnCompletion?.amount || 0,
      pointsExpirationType: config.pointsOnCompletion?.expiration?.type || 'never',
      pointsExpirationValue: config.pointsOnCompletion?.expiration?.value || 60,
      expirationType: config.expiration?.type || 'relative',
      expirationValue: config.expiration?.value || 30,
      expiresAt: config.expiresAt ? new Date(config.expiresAt).toISOString().split('T')[0] : '',
      steps: mappedSteps
    });
    setShowForm(true);
    setMessage(null);
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this flow configuration?')) {
      return;
    }

    try {
      await api.deletePartnerFlowConfig(partner._id, configId);
      setMessage({ type: 'success', text: 'Flow configuration deleted successfully' });
      fetchFlowConfigs();
    } catch (err) {
      console.error('Error deleting flow config:', err);
      setMessage({ type: 'error', text: 'Failed to delete flow configuration' });
    }
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [
        ...formData.steps,
        {
          goal: '',
          description: '',
          navigation: '',
          instructions: '',
          url: '',
          type: 'action',
          stepType: 'action',
          actionConfigId: '',
          criteria: {
            type: 'equal',
            value: 1,
            maxValue: 5
          },
          pointsOnCompletion: {
            amount: 0,
            expiration: {
              type: 'never',
              value: 0
            }
          },
          expirationOffset: null
        }
      ]
    });
  };

  const removeStep = (index) => {
    setFormData({
      ...formData,
      steps: formData.steps.filter((_, i) => i !== index)
    });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newSteps[index] = {
        ...newSteps[index],
        [parent]: {
          ...newSteps[index][parent],
          [child]: value
        }
      };
    } else {
      newSteps[index] = {
        ...newSteps[index],
        [field]: value
      };
    }
    setFormData({ ...formData, steps: newSteps });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim all string fields
    const trimmedName = formData.name.trim();
    const trimmedDescription = formData.description.trim();
    const trimmedShortText = formData.shortText.trim();

    // Validate basic fields
    if (!trimmedName) {
      setMessage({ type: 'error', text: 'Flow name is required' });
      return;
    }

    if (!trimmedDescription) {
      setMessage({ type: 'error', text: 'Flow description is required' });
      return;
    }

    if (!trimmedShortText) {
      setMessage({ type: 'error', text: 'Short text is required' });
      return;
    }

    // Validate expiration
    if (formData.expirationType === 'relative') {
      const expirationValue = parseInt(formData.expirationValue);
      if (!expirationValue || expirationValue < 1) {
        setMessage({ type: 'error', text: 'Flow expiration must be at least 1 day' });
        return;
      }
    } else if (formData.expirationType === 'absolute') {
      if (!formData.expiresAt) {
        setMessage({ type: 'error', text: 'Flow expiration date is required' });
        return;
      }
    }

    // Validate steps
    if (formData.steps.length === 0) {
      setMessage({ type: 'error', text: 'At least one step is required' });
      return;
    }

    // Validate each step
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      const stepNum = i + 1;

      if (!step.goal || !step.goal.trim()) {
        setMessage({ type: 'error', text: `Step ${stepNum}: Goal is required` });
        return;
      }

      if (!step.description || !step.description.trim()) {
        setMessage({ type: 'error', text: `Step ${stepNum}: Description is required` });
        return;
      }

      if (!step.instructions || !step.instructions.trim()) {
        setMessage({ type: 'error', text: `Step ${stepNum}: Instructions are required` });
        return;
      }

      // Validate action config for action steps
      if ((step.type === 'action' || step.stepType === 'action') && !step.actionConfigId) {
        setMessage({ type: 'error', text: `Step ${stepNum}: Action config is required for action steps` });
        return;
      }

      // Validate criteria
      if (!step.criteria.value || parseInt(step.criteria.value) < 1) {
        setMessage({ type: 'error', text: `Step ${stepNum}: Criteria value must be at least 1` });
        return;
      }

      if (step.criteria.type === 'range') {
        const maxValue = parseInt(step.criteria.maxValue);
        const value = parseInt(step.criteria.value);
        if (!maxValue || maxValue < 1) {
          setMessage({ type: 'error', text: `Step ${stepNum}: Max value is required for range criteria` });
          return;
        }
        if (maxValue <= value) {
          setMessage({ type: 'error', text: `Step ${stepNum}: Max value must be greater than value` });
          return;
        }
      }

      // Validate step expiration offset if specified
      if (step.expirationOffset !== null && step.expirationOffset !== undefined && step.expirationOffset !== '') {
        const offset = parseInt(step.expirationOffset);
        if (offset < 0) {
          setMessage({ type: 'error', text: `Step ${stepNum}: Expiration offset cannot be negative` });
          return;
        }
        // Check against flow expiration if it's relative
        if (formData.expirationType === 'relative') {
          const flowExpiration = parseInt(formData.expirationValue);
          if (offset > flowExpiration) {
            setMessage({ type: 'error', text: `Step ${stepNum}: Expiration offset (${offset} days) cannot exceed flow expiration (${flowExpiration} days)` });
            return;
          }
        }
      }

      // Validate points expiration if points are specified
      if (step.pointsOnCompletion && step.pointsOnCompletion.amount > 0) {
        if (step.pointsOnCompletion.expiration?.type === 'relative') {
          const expirationValue = parseInt(step.pointsOnCompletion.expiration.value);
          if (!expirationValue || expirationValue < 1) {
            setMessage({ type: 'error', text: `Step ${stepNum}: Points expiration must be at least 1 day` });
            return;
          }
        }
      }
    }

    try {
      // Build points on completion config
      let flowPointsConfig = null;
      if (formData.pointsOnCompletion && parseInt(formData.pointsOnCompletion) > 0) {
        flowPointsConfig = {
          amount: parseInt(formData.pointsOnCompletion),
          expiration: {
            type: formData.pointsExpirationType || 'never'
          }
        };
        
        // Add value based on expiration type
        if (formData.pointsExpirationType === 'relative') {
          flowPointsConfig.expiration.value = parseInt(formData.pointsExpirationValue) || 60;
        } else if (formData.pointsExpirationType === 'absolute') {
          flowPointsConfig.expiration.value = new Date(formData.pointsExpirationValue);
        }
      }

      const payload = {
        name: trimmedName,
        description: trimmedDescription,
        shortText: trimmedShortText,
        sequential: formData.isSequential,
        canRerun: formData.canRerun,
        steps: formData.steps.map(step => {
          const stepData = {
            goal: step.goal.trim(),
            description: step.description.trim(),
            instructions: step.instructions.trim(),
            url: step.url.trim(),
            stepType: step.type || step.stepType,
            criteria: {
              type: step.criteria.type,
              value: parseInt(step.criteria.value),
            }
          };

          // Add navigation if provided
          if (step.navigation && step.navigation.trim()) {
            stepData.navigation = step.navigation.trim();
          }

          // Add actionConfigId for action steps
          if (stepData.stepType === 'action') {
            stepData.actionConfigId = step.actionConfigId;
          }

          // Add maxValue for range criteria
          if (step.criteria.type === 'range') {
            stepData.criteria.maxValue = parseInt(step.criteria.maxValue);
          }

          // Add points configuration if specified
          if (step.pointsOnCompletion) {
            const amount = parseInt(step.pointsOnCompletion.amount) || 0;
            if (amount > 0) {
              stepData.pointsOnCompletion = {
                amount,
                expiration: {
                  type: step.pointsOnCompletion.expiration?.type || 'never',
                  value: step.pointsOnCompletion.expiration?.type === 'relative' 
                    ? (parseInt(step.pointsOnCompletion.expiration.value) || 30)
                    : (step.pointsOnCompletion.expiration?.value || 0)
                }
              };
            }
          }

          // Add expiration offset if specified
          if (step.expirationOffset !== null && step.expirationOffset !== undefined && step.expirationOffset !== '') {
            stepData.expirationOffset = parseInt(step.expirationOffset);
          }

          return stepData;
        })
      };

      // Add flow points configuration
      if (flowPointsConfig) {
        payload.pointsOnCompletion = flowPointsConfig;
      }

      // Handle flow expiration
      if (formData.expirationType === 'relative') {
        payload.expiration = {
          type: 'relative',
          value: parseInt(formData.expirationValue)
        };
      } else if (formData.expirationType === 'absolute') {
        payload.expiration = {
          type: 'absolute',
          value: new Date(formData.expiresAt)
        };
      }

      if (selectedConfig) {
        await api.updatePartnerFlowConfig(partner._id, selectedConfig._id, payload);
        setMessage({ type: 'success', text: 'Flow configuration updated successfully' });
      } else {
        await api.createPartnerFlowConfig(partner._id, payload);
        setMessage({ type: 'success', text: 'Flow configuration created successfully' });
      }

      setShowForm(false);
      fetchFlowConfigs();
    } catch (err) {
      console.error('Error saving flow config:', err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save flow configuration' });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedConfig(null);
  };

  const handleReorder = async (configId, direction) => {
    try {
      const response = await api.reorderPartnerFlowConfig(partner._id, configId, direction);
      if (response.data.success) {
        setMessage({ type: 'success', text: `Flow moved ${direction}` });
        // Update local state with reordered configs
        setFlowConfigs(response.data.flowConfigs);
      }
    } catch (err) {
      console.error('Error reordering flow config:', err);
      const errorMsg = err.response?.data?.error || 'Failed to reorder flow';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>
          Flow Configurations
        </h2>
        {!showForm && (
          <button
            onClick={handleCreate}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            + Create Flow Config
          </button>
        )}
      </div>

      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '6px',
          backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          {message.text}
        </div>
      )}

      {showForm ? (
        <div style={{
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          maxHeight: '600px',
          overflowY: 'auto'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>
            {selectedConfig ? 'Edit Flow Configuration' : 'Create Flow Configuration'}
          </h3>

          <form onSubmit={handleSubmit}>
            {/* Basic Info */}
            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>
                Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={inputStyle}
                placeholder="Enter flow configuration name"
                required
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>
                Description <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{ ...inputStyle, minHeight: '60px' }}
                placeholder="Describe what this flow helps users accomplish"
                required
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>
                Short Text <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.shortText}
                onChange={(e) => setFormData({ ...formData, shortText: e.target.value })}
                style={inputStyle}
                placeholder="Brief summary for display"
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isSequential}
                  onChange={(e) => setFormData({ ...formData, isSequential: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Sequential</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.canRerun}
                  onChange={(e) => setFormData({ ...formData, canRerun: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Can Rerun</span>
              </label>
            </div>

            {/* Flow Expiration */}
            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>Flow Expiration Type</label>
              <select
                value={formData.expirationType}
                onChange={(e) => setFormData({ ...formData, expirationType: e.target.value })}
                style={inputStyle}
              >
                <option value="relative">Relative (Days)</option>
                <option value="absolute">Absolute Date</option>
              </select>
            </div>

            {formData.expirationType === 'relative' ? (
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Expiration (Days)</label>
                <input
                  type="number"
                  value={formData.expirationValue}
                  onChange={(e) => setFormData({ ...formData, expirationValue: e.target.value })}
                  style={inputStyle}
                  min="1"
                  required
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                  How many days the flow remains valid after assignment
                </small>
              </div>
            ) : (
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Expires At</label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>
            )}

            {/* Flow Points */}
            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>Points on Completion</label>
              <input
                type="number"
                value={formData.pointsOnCompletion}
                onChange={(e) => setFormData({ ...formData, pointsOnCompletion: e.target.value })}
                style={inputStyle}
                min="0"
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                Points awarded when all steps are completed
              </small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>Points Expiration Type</label>
              <select
                value={formData.pointsExpirationType}
                onChange={(e) => setFormData({ ...formData, pointsExpirationType: e.target.value })}
                style={inputStyle}
              >
                <option value="never">Never</option>
                <option value="relative">Relative (Days)</option>
                <option value="absolute">Absolute Date</option>
              </select>
            </div>

            {formData.pointsExpirationType === 'relative' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Points Expiration (Days)</label>
                <input
                  type="number"
                  value={formData.pointsExpirationValue}
                  onChange={(e) => setFormData({ ...formData, pointsExpirationValue: e.target.value })}
                  style={inputStyle}
                  min="1"
                />
                <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                  How many days after earning until points expire
                </small>
              </div>
            )}

            {formData.pointsExpirationType === 'absolute' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={labelStyle}>Points Expire At</label>
                <input
                  type="date"
                  value={formData.pointsExpirationValue}
                  onChange={(e) => setFormData({ ...formData, pointsExpirationValue: e.target.value })}
                  style={inputStyle}
                />
              </div>
            )}

            {/* Steps */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={labelStyle}>Steps</label>
                <button
                  type="button"
                  onClick={addStep}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  + Add Step
                </button>
              </div>

              {formData.steps.map((step, index) => (
                <div key={index} style={{
                  padding: '15px',
                  marginBottom: '10px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <strong style={{ fontSize: '14px' }}>Step {index + 1}</strong>
                    <button
                      type="button"
                      onClick={() => removeStep(index)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ ...labelStyle, fontSize: '12px' }}>
                        Goal <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={step.goal}
                        onChange={(e) => updateStep(index, 'goal', e.target.value)}
                        style={{ ...inputStyle, fontSize: '13px' }}
                        placeholder="What should the user accomplish?"
                        required
                      />
                    </div>
                    <div>
                      <label style={{ ...labelStyle, fontSize: '12px' }}>Type</label>
                      <select
                        value={step.type}
                        onChange={(e) => updateStep(index, 'type', e.target.value)}
                        style={{ ...inputStyle, fontSize: '13px' }}
                      >
                        <option value="action">Action</option>
                        <option value="manual">Manual</option>
                      </select>
                    </div>
                  </div>

                  {step.type === 'action' && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ ...labelStyle, fontSize: '12px' }}>
                        Action Config <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <select
                        value={step.actionConfigId || ''}
                        onChange={(e) => updateStep(index, 'actionConfigId', e.target.value)}
                        style={{ ...inputStyle, fontSize: '13px' }}
                        required
                      >
                        <option value="">Select an action config...</option>
                        {actionConfigs.map(config => (
                          <option key={config._id} value={config._id}>
                            {config.actionType} - {config.description || 'No description'}
                          </option>
                        ))}
                      </select>
                      {actionConfigs.length === 0 && (
                        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                          No action configs available. Create one in the Action Configs tab first.
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '10px' }}>
                    <label style={{ ...labelStyle, fontSize: '12px' }}>
                      Description <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={step.description}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                      style={{ ...inputStyle, fontSize: '13px' }}
                      placeholder="Brief description of this step"
                      required
                    />
                  </div>

                  <div style={{ marginTop: '10px' }}>
                    <label style={{ ...labelStyle, fontSize: '12px' }}>
                      Navigation
                    </label>
                    <input
                      type="text"
                      value={step.navigation || ''}
                      onChange={(e) => updateStep(index, 'navigation', e.target.value)}
                      style={{ ...inputStyle, fontSize: '13px' }}
                      placeholder="e.g., Dashboard > Events tab > Click on event"
                    />
                  </div>

                  <div style={{ marginTop: '10px' }}>
                    <label style={{ ...labelStyle, fontSize: '12px' }}>
                      Instructions <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                      value={step.instructions}
                      onChange={(e) => updateStep(index, 'instructions', e.target.value)}
                      style={{ ...inputStyle, fontSize: '13px', minHeight: '60px' }}
                      placeholder="Provide detailed instructions for completing this step"
                      required
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginTop: '10px' }}>
                    <div>
                      <label style={{ ...labelStyle, fontSize: '12px' }}>Criteria Type</label>
                      <select
                        value={step.criteria.type}
                        onChange={(e) => updateStep(index, 'criteria.type', e.target.value)}
                        style={{ ...inputStyle, fontSize: '13px' }}
                      >
                        <option value="equal">Equal</option>
                        <option value="equalOrGreater">Equal or Greater</option>
                        <option value="range">Range</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ ...labelStyle, fontSize: '12px' }}>Value</label>
                      <input
                        type="number"
                        value={step.criteria.value}
                        onChange={(e) => updateStep(index, 'criteria.value', e.target.value)}
                        style={{ ...inputStyle, fontSize: '13px' }}
                        min="1"
                      />
                    </div>
                    {step.criteria.type === 'range' && (
                      <div>
                        <label style={{ ...labelStyle, fontSize: '12px' }}>Max Value</label>
                        <input
                          type="number"
                          value={step.criteria.maxValue}
                          onChange={(e) => updateStep(index, 'criteria.maxValue', e.target.value)}
                          style={{ ...inputStyle, fontSize: '13px' }}
                          min="1"
                        />
                      </div>
                    )}
                  </div>

                  {/* Step Points Configuration */}
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', fontWeight: '600' }}>
                      Step Points (Optional)
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize: '12px' }}>Points Amount</label>
                        <input
                          type="number"
                          value={step.pointsOnCompletion?.amount || 0}
                          onChange={(e) => {
                            const amount = parseInt(e.target.value) || 0;
                            updateStep(index, 'pointsOnCompletion', {
                              ...step.pointsOnCompletion,
                              amount,
                              expiration: step.pointsOnCompletion?.expiration || { type: 'never', value: 0 }
                            });
                          }}
                          style={{ ...inputStyle, fontSize: '13px' }}
                          min="0"
                        />
                      </div>
                      
                      <div>
                        <label style={{ ...labelStyle, fontSize: '12px' }}>Points Expiration</label>
                        <select
                          value={step.pointsOnCompletion?.expiration?.type || 'never'}
                          onChange={(e) => {
                            const type = e.target.value;
                            updateStep(index, 'pointsOnCompletion', {
                              amount: step.pointsOnCompletion?.amount || 0,
                              expiration: {
                                type,
                                value: type === 'never' ? 0 : (step.pointsOnCompletion?.expiration?.value || 30)
                              }
                            });
                          }}
                          style={{ ...inputStyle, fontSize: '13px' }}
                        >
                          <option value="never">Never</option>
                          <option value="relative">Relative (Days)</option>
                        </select>
                      </div>
                    </div>

                    {step.pointsOnCompletion?.expiration?.type === 'relative' && (
                      <div style={{ marginTop: '10px' }}>
                        <label style={{ ...labelStyle, fontSize: '12px' }}>Days Until Expiration</label>
                        <input
                          type="number"
                          value={step.pointsOnCompletion?.expiration?.value || 30}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 30;
                            updateStep(index, 'pointsOnCompletion', {
                              amount: step.pointsOnCompletion?.amount || 0,
                              expiration: {
                                type: 'relative',
                                value
                              }
                            });
                          }}
                          style={{ ...inputStyle, fontSize: '13px' }}
                          min="1"
                        />
                      </div>
                    )}
                  </div>

                  {/* Step Expiration Offset */}
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ ...labelStyle, fontSize: '12px' }}>
                      Step Expiration Offset (Optional)
                    </label>
                    <input
                      type="number"
                      value={step.expirationOffset || ''}
                      onChange={(e) => updateStep(index, 'expirationOffset', e.target.value ? parseInt(e.target.value) : null)}
                      style={{ ...inputStyle, fontSize: '13px' }}
                      min="0"
                      placeholder="Leave empty to inherit flow expiration"
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '11px' }}>
                      Days from flow start when this step expires (empty = use flow expiration)
                    </small>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                {selectedConfig ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading flow configurations...
        </div>
      ) : flowConfigs.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          <p>No flow configurations found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {flowConfigs.map((config, index) => (
            <div
              key={config._id}
              style={{
                padding: '20px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start'
              }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#111827' }}>
                    {config.name}
                  </h3>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                    {config.description}
                  </p>
                  <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                    <span>{config.steps?.length || 0} steps</span>
                    {' • '}
                    <span>{
                      typeof config.pointsOnCompletion === 'object' && config.pointsOnCompletion?.amount !== undefined
                        ? config.pointsOnCompletion.amount
                        : (typeof config.pointsOnCompletion === 'number' ? config.pointsOnCompletion : 0)
                    } points</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Order buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <button
                      onClick={() => handleReorder(config._id, 'up')}
                      disabled={index === 0}
                      style={{
                        padding: '2px 6px',
                        backgroundColor: index === 0 ? '#f9fafb' : '#f3f4f6',
                        color: index === 0 ? '#d1d5db' : '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                        lineHeight: 1
                      }}
                      title="Move up"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleReorder(config._id, 'down')}
                      disabled={index === flowConfigs.length - 1}
                      style={{
                        padding: '2px 6px',
                        backgroundColor: index === flowConfigs.length - 1 ? '#f9fafb' : '#f3f4f6',
                        color: index === flowConfigs.length - 1 ? '#d1d5db' : '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: index === flowConfigs.length - 1 ? 'not-allowed' : 'pointer',
                        lineHeight: 1
                      }}
                      title="Move down"
                    >
                      ▼
                    </button>
                  </div>
                  
                  <button
                    onClick={() => onViewFlows && onViewFlows(config._id, config.name)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    View Flows →
                  </button>
                  <button
                    onClick={() => handleEdit(config)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(config._id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PartnerFlowConfigsTab;
